import React from 'react';
import {Masonry, Image, EaseIn} from 'polyphony/components/generic';
import {getAttachment} from 'polyphony/networking';
import {X, ChevronUp, ChevronDown} from 'polyphony/icons';
import {withNoProps} from 'polyphony/hoc';
import {useHover} from 'polyphony/hooks';

const svgs = {
    image: <svg viewBox="0 0 512 512"><path d="M464 448H48c-26.51 0-48-21.49-48-48V112c0-26.51 21.49-48 48-48h416c26.51 0 48 21.49 48 48v288c0 26.51-21.49 48-48 48zM112 120c-30.928 0-56 25.072-56 56s25.072 56 56 56 56-25.072 56-56-25.072-56-56-56zM64 384h384V272l-87.515-87.515c-4.686-4.686-12.284-4.686-16.971 0L208 320l-55.515-55.515c-4.686-4.686-12.284-4.686-16.971 0L64 336v48z"/></svg>,
    video: <svg viewBox="0 0 576 512"><path d="M336.2 64H47.8C21.4 64 0 85.4 0 111.8v288.4C0 426.6 21.4 448 47.8 448h288.4c26.4 0 47.8-21.4 47.8-47.8V111.8c0-26.4-21.4-47.8-47.8-47.8zm189.4 37.7L416 177.3v157.4l109.6 75.5c21.2 14.6 50.4-.3 50.4-25.8V127.5c0-25.4-29.1-40.4-50.4-25.8z"/></svg>,
    videoPlay: <svg viewBox="0 0 512 512"><path fill="currentColor" d="M371.7 238l-176-107c-15.8-8.8-35.7 2.5-35.7 21v208c0 18.4 19.8 29.8 35.7 21l176-101c16.4-9.1 16.4-32.8 0-42zM504 256C504 119 393 8 256 8S8 119 8 256s111 248 248 248 248-111 248-248zm-448 0c0-110.5 89.5-200 200-200s200 89.5 200 200-89.5 200-200 200S56 366.5 56 256z"/></svg>
};

const fStyles = {
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    fancybox: {
        overflow: 'hidden',
        width: '100%',
        height: '100%'
    },
    hover: {
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%'
    },
    playButton: {
        position: 'absolute',
        left: '33%',
        right: '33%',
        top: '33%',
        bottom: '33%',
        height: '33%',
        width: '33%'
    },
    placeholderImage: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    }
};

const insertElements = (array, idx, element = undefined, replace = 0) => {
    let insertion = element === undefined ? [] : Array.isArray(element) ? element : [element];
    return [...array.slice(0, idx), ...insertion, ...array.slice(idx + replace, array.length)];
};
const deleteElement = (array, idx) => insertElements(array, idx, undefined, 1);
const changeOrder = (ary, at, by) => {
    ary = [...ary];
    let ctr = by;
    let isNeg = by < 0;

    while (ctr !== 0) {
        // Calculate the indeces
        let idx = at + (by - ctr);
        let nextIdx = isNeg ? idx - 1 : idx + 1;

        // swap the array entries
        let tmp = ary[nextIdx];
        ary[nextIdx] = ary[idx];
        ary[idx] = tmp;

        // Advance the counter toward 0
        isNeg ? ctr++ : ctr--;
    }
    return ary;
};

const FancyboxWrapper = ({url, children}) =>  <a href={`${url}`} className='polyphony-fancybox'>{children}</a>;

const OrderingButton = ({icon, onClick, isEnabled}) => {
    const [{hover}, ref] = useHover();
    return <div ref={ref} onClick={isEnabled ? onClick : undefined} style={!isEnabled ? {userInteraction: 'none', cursorEvents: 'none', color: 'lightgray'} : {cursor: 'pointer', color: hover ? 'gray' : 'black'}}>
                {icon}
            </div>;
};

const MasonryGalleryEditor = () => {
    const {unitsWide, images, setImages, setUnitsWide} = useGallery();

    const {BaseMediaPicker} = polyphony.components.backend;

    const onChangeUnitsWide = e => setUnitsWide(parseInt(e.target.value));

    const addImages = img => {
        let x = Array.isArray(img) ? img : [img];
        setImages([...images, ...x]);
    };

    const refreshImages = () => {
        Promise.all(images.map(x => {
            const {widthUnits, heightUnits, placeholderImage: placeholder, id} = x;
            let placeholderId = placeholder ? placeholder.id : null;
            return getAttachment(id).then(xp => {
                if (placeholderId) {
                    return getAttachment(placeholderId).then(pi => ({...xp, widthUnits, heightUnits, placeholderImage: pi}));
                }
                return {...xp, widthUnits, heightUnits};
            });
        })).then(setImages);
    };

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', flexFlow: 'row nowrap'}}>
                <div>
                    <input type='text' onChange={onChangeUnitsWide} value={unitsWide} style={{width: '3em', textAlign: 'center', margin: 10}}/>
                    <span>units wide</span>
                </div>
                <div>
                    <a style={{cursor: 'pointer'}} onClick={refreshImages}>Refresh Attachments</a>
                </div>
            </div>
            <div style={{display: 'flex', flexFlow: 'column nowrap'}}>
                {images.map((img, i) => {
                    const setAttr = (k, v) => setImages(insertElements(images, i, {...img, [k]: v}, 1));
                    const updateXUnits = e => setAttr('widthUnits', e.target.value);
                    const updateYUnits = e => setAttr('heightUnits', e.target.value);
                    const setPlaceholderImage = x => setAttr('placeholderImage', x);

                    const deleteMe = () => setImages(deleteElement(images, i));
                    const moveUp = () => setImages(changeOrder(images, i, -1));
                    const moveDown = () => setImages(changeOrder(images, i, 1));

                    const imageCount = images.length;
                    const isFirst = i === 0;
                    const isLast = i === imageCount - 1;
                    const replaceImage = newIm => setImages(images.map(x => x.id === img.id ? {...newIm, widthUnits: x.widthUnits, heightUnits: x.heightUnits} : x));

                    const {filename, widthUnits, heightUnits, type, placeholderImage} = img;

                    const isVideo = type === 'video';

                    return <div key={`${img.id} ${i}`} style={{height: 60, display: 'flex', flexFlow: 'row nowrap', justifyContent: 'space-between', alignItems: "center"}}>
                        <div style={{display: 'flex', flexFlow: 'row nowrap', justifyContent: 'flex-start', alignItems: "center"}}>
                            {/* Ordering Controls */}
                            <div>
                                <OrderingButton icon={<ChevronUp width={22} height={15} />}
                                             onClick={ moveUp}
                                           isEnabled={!isFirst} />
                                <OrderingButton icon={<ChevronDown width={22} height={15} />}
                                             onClick={moveDown}
                                           isEnabled={!isLast} />
                            </div>

                            {/* Primary Upload */}
                            <BaseMediaPicker selectedMedia={img} onSelect={replaceImage}>
                            {
                                open => <a onClick={open} style={{margin: 10, cursor: 'pointer'}}>
                                    {isVideo
                                       ? <div style={{width: 44, height: '100%'}}>{svgs.video}</div>
                                       : <Image preferredSize='thumbnail' media={img} width={44} height={44} style={{maxWidth: 'none', objectFit: 'cover', borderRadius: 5}}/>}</a>
                            }
                            </BaseMediaPicker>

                            {/* Placeholder Image upload */}
                            {isVideo && <BaseMediaPicker selectedMedia={placeholderImage} type={['image']} onSelect={setPlaceholderImage}>
                            {
                                open => <a onClick={open} style={{margin: 10, cursor: 'pointer'}}>
                                    {placeholderImage
                                       ? <Image preferredSize="thumbnail" media={placeholderImage} width={44} height={'100%'} style={{objectFit: 'cover', borderRadius: 5, maxWidth: 'none'}}/>
                                       : <div style={{width: 44, height: '100%'}}>{svgs.image}</div>}
                                </a>
                            }
                            </BaseMediaPicker>}
                            <div style={{padding: 10, boxSizing: 'border-box'}}>{filename}</div>
                        </div>

                        <div style={{display: 'flex', flexFlow: 'row nowrap', justifyContent: 'flex-start', alignItems: "center"}}>
                            <input type="text" style={{width: '3em'}} value={widthUnits ? widthUnits : 1} onChange={updateXUnits} />
                            <span>&nbsp;x&nbsp;</span>
                            <input type="text" style={{width: '3em'}} value={heightUnits ? heightUnits : 1} onChange={updateYUnits} />

                            <div alt="Delete Tile" onClick={deleteMe} style={{cursor: 'pointer', width: 22, height: 22, padding: 11, boxSizing: 'content-box', color: 'red'}}>
                                <X width='100%' height="100%" />
                            </div>
                        </div>
                    </div>;
            })}
            </div>
            <BaseMediaPicker onSelect={addImages} multiple>
            {open => <a onClick={open} style={{cursor: 'pointer'}} className='polyphony-gallery-add-button'>Add Media</a>}
            </BaseMediaPicker>
        </div>
    );
};

const MasonryGalleryContext = React.createContext();
const useGallery = () => React.useContext(MasonryGalleryContext);

const GalleryTileContent = ({media}) => {
    const [{hover}, ref] = useHover();
    const animate = EaseIn.useAnimate();
    const {type, url, placeholderImage} = media;
    const isImage = type === 'image';
    const isVideo = type === 'video';

    return isImage ? <Image lazy onLoad={animate} media={media} style={fStyles.image}/>
                                  : isVideo ? <FancyboxWrapper url={url} style={fStyles.fancybox}>
                                                  <div ref={ref} style={fStyles.hover}>
                                                      {placeholderImage ? <Image lazy onLoad={animate} media={placeholderImage} style={{...fStyles.placeholderImage, opacity: hover ? 0.3 : 1}} /> : animate()}
                                                      <div style={{...fStyles.playButton, color: hover ? 'black' : 'white'}}>{svgs.videoPlay}</div>
                                                  </div>
                                              </FancyboxWrapper>
                                            : animate();
};

const MasonryGalleryFrontend = withNoProps(() => {
    const {unitsWide, images} = useGallery();

    return <Masonry unitsWide={unitsWide} style={{width: '100%'}}>
        {images.map((media, i) => {
            const {id, widthUnits, heightUnits} = media;
            return <Masonry.Tile key={`${id} ${i}`} widthUnits={widthUnits ? widthUnits : 1} heightUnits={heightUnits ? heightUnits : 1}>
                <EaseIn>
                    <GalleryTileContent media={media} />
                </EaseIn>
            </Masonry.Tile>;
        })}
    </Masonry>;
});

const MasonryGallery = ({gutenberg:{setProp, isEditor}, images=[], unitsWide=4}) =>
    <MasonryGalleryContext.Provider value={{
        images,
        unitsWide,
        setImages: isEditor ? x => setProp('images', x) : undefined,
        setUnitsWide: isEditor ? x => setProp('unitsWide', x) : undefined}}>
        {isEditor ? <MasonryGalleryEditor /> : <MasonryGalleryFrontend />}
    </MasonryGalleryContext.Provider>;

MasonryGallery.slug = 'masonrygallery';
MasonryGallery.title = "Masonry Gallery";
MasonryGallery.icon = 'format-image';
MasonryGallery.category = 'common';
//MasonryGallery.storage_key = 'data';

polyphony.addBlock(MasonryGallery);

